import { useState, useEffect, useRef } from 'react';
import type { Asset } from 'contentful';
import cx from 'classnames';
import Image from 'next/image';
import downIcon from '@/images/ic_down_icon.svg';
import { getImageDataWithHttps } from '~/utils/contentful';
import { ISubNavigationFields } from '@/types/contentful';
import { usePageDataContext } from '~/context/PageData';
import { trackWithFingerprint } from '~/utils/trackWithFingerprint';

export function SubNavigation(props: ISubNavigationFields) {
  const [activeTab, setActiveTab] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState<string | null>(null);
  const [selectedIcon, setSelectedIcon] = useState<Asset | null>(null);
  const scrollTimeout = useRef<NodeJS.Timeout | null>(null);
  const { query: { gl_content, g_adgroupid, utm_source } = {} } =
    usePageDataContext();

  // Get header height and offset for current breakpoint
  const getHeaderAndOffset = () => {
    const header = document.querySelector('header') as HTMLElement;
    const headerHeight = header ? header.offsetHeight : 0;
    const clickOffset = 40;
    return { headerHeight, clickOffset };
  };

  const handleClick = (anchor: string) => {
    setActiveTab(anchor);
    const element = document.getElementById(anchor);
    if (element) {
      const { headerHeight, clickOffset } = getHeaderAndOffset();
      const elementPosition = element.offsetTop - headerHeight - clickOffset;
      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth'
      });
    }
  };

  const handleTabClick = (
    anchorLink: string,
    label: string,
    pictogram?: Asset
  ) => {
    handleClick(anchorLink);
    setSelectedLabel(label);
    setSelectedIcon(pictogram || null);
    setIsExpanded(false);
  };

  // Scroll spy: update activeTab as user scrolls
  useEffect(() => {
    if (!props.navLinks) return;
    const handleScroll = () => {
      if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
      scrollTimeout.current = setTimeout(() => {
        let activeIdx = -1;
        const midpoint = window.scrollY + window.innerHeight / 2;
        for (let i = 0; i < props.navLinks.length; i++) {
          const tab = props.navLinks[i];
          const el = document.getElementById(tab.fields.anchorLink);
          if (el) {
            const sectionTop = el.offsetTop;
            if (sectionTop <= midpoint) {
              activeIdx = i;
            }
          }
        }
        if (activeIdx !== -1) {
          const tab = props.navLinks[activeIdx];
          setActiveTab(tab.fields.anchorLink);
          setSelectedLabel(tab.fields.title);
          setSelectedIcon(tab.fields.pictogram || null);
        } else {
          setActiveTab(null);
        }
      }, 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
    };
  }, [props.navLinks]);

  if (
    (!props.keyword && !props.adgroupId && !props.channels) ||
    (props.keyword === gl_content &&
      (!g_adgroupid ||
        (props.adgroupId && props.adgroupId.includes(g_adgroupid))) &&
      props.channels?.includes(utm_source as string))
  ) {
    return (
      <>
        {/* Mobile / Tablet SubNav */}
        <div className="relative z-30 w-full desktop:hidden">
          <div className="w-full">
            <button
              className={cx(
                'flex w-full items-center justify-between border-y border-line bg-primary-surface px-5 py-4'
              )}
              onClick={() => setIsExpanded((prev) => !prev)}
            >
              <span className="flex items-center gap-2 text-base font-medium">
                {selectedIcon && (
                  <Image
                    {...getImageDataWithHttps(selectedIcon)}
                    width={20}
                    height={20}
                  />
                )}
                {selectedLabel || props?.header}
              </span>
              <div className="flex items-center justify-center px-3">
                <Image
                  src={downIcon}
                  alt="Toggle menu"
                  width={20}
                  height={20}
                  className={cx('transition-transform duration-300', {
                    'rotate-180': isExpanded
                  })}
                />
              </div>
            </button>
            <div
              className={cx(
                'flex flex-col divide-y divide-line overflow-hidden bg-primary-surface transition-all duration-300 ease-in-out',
                isExpanded
                  ? 'max-h-[500px] border-b border-line opacity-100'
                  : 'max-h-0 opacity-0'
              )}
            >
              {props.navLinks?.map((tab) => {
                const isActive = activeTab === tab.fields.anchorLink;
                return (
                  <button
                    key={tab.sys.id}
                    onClick={() => {
                      handleTabClick(
                        tab.fields.anchorLink,
                        tab.fields.title,
                        tab.fields.pictogram
                      );
                      trackWithFingerprint('CTA Clicked', {
                        module: 'subnavigation',
                        value: tab.fields.title,
                        page_url: window.location.href,
                        device_type: 'mobile'
                      });
                    }}
                    className={cx(
                      'flex w-full items-center gap-3 px-5 py-4 text-left text-base font-medium transition-all hover:bg-secondary',
                      {
                        'bg-secondary text-on-primary-surface': isActive,
                        'bg-white text-on-primary-surface': !isActive
                      }
                    )}
                  >
                    {tab.fields.pictogram && (
                      <Image
                        {...getImageDataWithHttps(tab.fields.pictogram)}
                        width={20}
                        height={20}
                      />
                    )}
                    {tab.fields.title}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        {/* Default Desktop SubNav */}
        <div
          data-testid="desktop-nav"
          className="z-50 mt-0 hidden w-full justify-center border-y border-line bg-cool-neutral-100 px-4 py-6 shadow-md desktop:flex"
          style={{
            backgroundColor: props?.desktopBackgroundColor
              ? props?.desktopBackgroundColor
              : ''
          }}
        >
          <div className="flex space-x-3">
            {props.navLinks.map((tab) => (
              <button
                key={tab.sys.id}
                onClick={() => {
                  handleTabClick(
                    tab.fields.anchorLink,
                    tab.fields.title,
                    tab.fields.pictogram
                  );
                  trackWithFingerprint('CTA Clicked', {
                    module: 'subnavigation',
                    value: tab.fields.title,
                    page_url: window.location.href,
                    device_type: 'desktop'
                  });
                }}
                className={cx(
                  'h-10 whitespace-nowrap rounded-full border-2 px-4 text-base font-medium transition-all',
                  activeTab === tab.fields.anchorLink
                    ? 'border-tertiary bg-tertiary text-black shadow-sm'
                    : 'bg-transparent border-[#E3E3E3] text-on-primary-surface hover:border-tertiary'
                )}
              >
                {tab.fields.title}
              </button>
            ))}
          </div>
        </div>
      </>
    );
  } else {
    return null;
  }
}
